import React, { useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputAdornment from '~/components/core/Atomic/InputAdornment';
import MenuItem from '~/components/core/Atomic/MenuItem';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { EyeIcon, EyeSlashIcon } from '~/components/icons';
import TextFieldFormik, { NumericTextFieldFormik } from '~/components/TextFieldFormik';
import {
  SorConfigurationParamsSorAuthMode,
  SorConfigurationResponseSorAuthMode,
} from '~/server_shared/openapi/data-contracts';

import TEXTS from './texts';
import type { SorConfiguration, SorConfigurationParams, SorOperation, SorSetupFormValues } from './types';

interface SorSetupDialogProps {
  /** The saved configuration to edit; null sets up a new one. */
  configuration: SorConfiguration | null;
  operations: SorOperation[];
  /** Whether the organization's SSO includes offline_access, without which no user has an SSO token for the SoR. */
  isSsoOfflineAccessEnabled: boolean;
  onSubmit: (params: SorConfigurationParams) => Promise<void>;
  onClose: () => void;
}

// An RFC 9110 token, and the headers CMS sets on its calls to the SoR, as the server checks them.
const HEADER_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const RESERVED_HEADER_NAMES = new Set(['authorization', 'idempotency-key', 'content-type', 'accept', 'host']);

// The server's bounds. A blank number is sent as null, which keeps the saved value, or takes the
// server's default for a new configuration.
const timeoutSchema = Yup.number().min(1, TEXTS.dialog.timeoutRange).max(30, TEXTS.dialog.timeoutRange).nullable();

const validationSchema = Yup.object().shape({
  display_name: Yup.string().trim().required(TEXTS.dialog.required),
  api_base_url: Yup.string().trim().required(TEXTS.dialog.required),
  // The server refuses the user-token mode without a scope to request each user's token for.
  sso_token_scope: Yup.string().when('auth_mode', {
    is: SorConfigurationParamsSorAuthMode.UserSsoToken,
    then: (schema) => schema.trim().required(TEXTS.dialog.required),
  }),
  // The server's rules for the additional header: a name and a value while it is on, the name an HTTP header name
  // that is none of the headers CMS sets itself.
  extra_header_name: Yup.string().when('extra_header_enabled', {
    is: true,
    then: (schema) =>
      schema
        .trim()
        .required(TEXTS.dialog.required)
        .matches(HEADER_NAME_PATTERN, TEXTS.dialog.invalidHeaderName)
        .test(
          'not-reserved',
          TEXTS.dialog.reservedHeaderName,
          (name) => !RESERVED_HEADER_NAMES.has((name ?? '').toLowerCase())
        ),
  }),
  extra_header_value: Yup.string().when('extra_header_enabled', {
    is: true,
    then: (schema) => schema.trim().required(TEXTS.dialog.required),
  }),
  connect_timeout_seconds: timeoutSchema,
  read_timeout_seconds: timeoutSchema,
  max_tries: Yup.number().min(1, TEXTS.dialog.maxTriesMinimum).nullable(),
});

// The generator emits a copy of the enum per schema: the saved configuration carries the response's, and the
// PUT takes the params'.
const AUTH_MODE_PARAMS: Record<SorConfigurationResponseSorAuthMode, SorConfigurationParamsSorAuthMode> = {
  [SorConfigurationResponseSorAuthMode.StaticToken]: SorConfigurationParamsSorAuthMode.StaticToken,
  [SorConfigurationResponseSorAuthMode.UserSsoToken]: SorConfigurationParamsSorAuthMode.UserSsoToken,
};

const getInitialValues = (configuration: SorConfiguration | null, operations: SorOperation[]): SorSetupFormValues => ({
  display_name: configuration?.display_name ?? '',
  api_base_url: configuration?.api_base_url ?? '',
  // The server ignores its own placeholder, so sending it back unchanged keeps the saved token.
  api_token: configuration?.api_token_output ?? '',
  auth_mode: configuration ? AUTH_MODE_PARAMS[configuration.auth_mode] : SorConfigurationParamsSorAuthMode.StaticToken,
  sso_token_scope: configuration?.sso_token_scope ?? '',
  extra_header_enabled: configuration?.extra_header_enabled ?? false,
  extra_header_name: configuration?.extra_header_name ?? '',
  // Sent back as the placeholder too, which keeps the saved value.
  extra_header_value: configuration?.extra_header_value_output ?? '',
  connect_timeout_seconds: configuration?.connect_timeout_seconds ?? null,
  read_timeout_seconds: configuration?.read_timeout_seconds ?? null,
  max_tries: configuration?.max_tries ?? null,
  operations: Object.fromEntries(
    operations.map(({ operation }) => [operation, Boolean(configuration?.enabled_operations.includes(operation))])
  ),
});

// Built from the offered operations, so a saved one no module contributes any more is dropped:
// the server refuses an operation it has no handler for.
const toConfigurationParams = (
  {
    operations: isCommittedAtSor,
    sso_token_scope,
    extra_header_enabled,
    extra_header_name,
    extra_header_value,
    ...settings
  }: SorSetupFormValues,
  operations: SorOperation[]
): SorConfigurationParams => ({
  ...settings,
  // Blank only in the static mode, which needs no scope; the server refuses a blank one, and null keeps the saved one.
  sso_token_scope: isEmpty(sso_token_scope.trim()) ? null : sso_token_scope,
  // Switching the header off clears its name and value on the server, so neither is sent then.
  extra_header_enabled,
  extra_header_name: extra_header_enabled ? extra_header_name : null,
  extra_header_value: extra_header_enabled ? extra_header_value : null,
  enabled_operations: operations
    .filter(({ operation }) => isCommittedAtSor[operation])
    .map(({ operation }) => operation),
});

const SorSetupDialog: React.FC<SorSetupDialogProps> = ({
  configuration,
  operations,
  isSsoOfflineAccessEnabled,
  onSubmit,
  onClose,
}) => (
  <Formik<SorSetupFormValues>
    initialValues={getInitialValues(configuration, operations)}
    validationSchema={validationSchema}
    onSubmit={(values) => onSubmit(toConfigurationParams(values, operations))}
  >
    <SorSetupForm
      isEdit={Boolean(configuration)}
      operations={operations}
      isSsoOfflineAccessEnabled={isSsoOfflineAccessEnabled}
      onClose={onClose}
    />
  </Formik>
);

interface SecretTextFieldFormikProps {
  id: string;
  label: string;
  showLabel: string;
  hideLabel: string;
}

// Masked until its eye button reveals it.
const SecretTextFieldFormik = ({ id, label, showLabel, hideLabel }: SecretTextFieldFormikProps) => {
  const { classes } = useStyles();
  const [isVisible, setIsVisible] = useState(false);

  return (
    <TextFieldFormik
      id={id}
      label={label}
      className={classes.textField}
      fullWidth
      InputProps={{
        type: isVisible ? 'text' : 'password',
        endAdornment: (
          <InputAdornment position="end">
            <IconButton aria-label={isVisible ? hideLabel : showLabel} onClick={() => setIsVisible(!isVisible)}>
              {isVisible ? <EyeIcon iconColor="currentColor" /> : <EyeSlashIcon iconColor="currentColor" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

interface SorSetupFormProps {
  isEdit: boolean;
  operations: SorOperation[];
  isSsoOfflineAccessEnabled: boolean;
  onClose: () => void;
}

const SorSetupForm = ({ isEdit, operations, isSsoOfflineAccessEnabled, onClose }: SorSetupFormProps) => {
  const { isSubmitting, values, setFieldValue } = useFormikContext<SorSetupFormValues>();
  const { classes } = useStyles();
  const isUserTokenMode = values.auth_mode === SorConfigurationParamsSorAuthMode.UserSsoToken;

  return (
    <CardDialog
      title={isEdit ? TEXTS.dialog.editTitle : TEXTS.dialog.createTitle}
      isDialog
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
    >
      <Form>
        <div className="grid gap-20">
          <TextFieldFormik id="display_name" label={TEXTS.dialog.displayName} className={classes.textField} fullWidth />
          <TextFieldFormik id="api_base_url" label={TEXTS.dialog.apiBaseUrl} className={classes.textField} fullWidth />
          <SingleSelectField<SorConfigurationParamsSorAuthMode>
            id="auth_mode"
            label={TEXTS.dialog.authentication}
            value={values.auth_mode}
            className={classes.textField}
            fullWidth
            onChange={(mode) => setFieldValue('auth_mode', mode)}
          >
            {Object.values(SorConfigurationParamsSorAuthMode).map((mode) => (
              <MenuItem key={mode} value={mode}>
                {TEXTS.authModes[mode]}
              </MenuItem>
            ))}
          </SingleSelectField>
          {isUserTokenMode ? (
            <>
              {!isSsoOfflineAccessEnabled && (
                <AlertBanner alertType={AlertBanner.ALERT_TYPES.WARNING} note={TEXTS.offlineAccessWarning} withIcon />
              )}
              <TextFieldFormik
                id="sso_token_scope"
                label={TEXTS.dialog.ssoTokenScope}
                className={classes.textField}
                fullWidth
              />
            </>
          ) : (
            <SecretTextFieldFormik
              id="api_token"
              label={TEXTS.dialog.apiToken}
              showLabel={TEXTS.dialog.showApiToken}
              hideLabel={TEXTS.dialog.hideApiToken}
            />
          )}
          <SwitchFormik id="extra_header_enabled" label={TEXTS.dialog.sendExtraHeader} hideErrorGap />
          {values.extra_header_enabled && (
            <>
              <TextFieldFormik
                id="extra_header_name"
                label={TEXTS.dialog.extraHeaderName}
                placeholder="Ocp-Apim-Subscription-Key"
                className={classes.textField}
                fullWidth
              />
              <SecretTextFieldFormik
                id="extra_header_value"
                label={TEXTS.dialog.extraHeaderValue}
                showLabel={TEXTS.dialog.showExtraHeaderValue}
                hideLabel={TEXTS.dialog.hideExtraHeaderValue}
              />
            </>
          )}
          <div className="grid grid-cols-2 gap-20">
            <NumericTextFieldFormik
              id="connect_timeout_seconds"
              label={TEXTS.dialog.connectTimeout}
              className={classes.textField}
              decimalScale={0}
              allowNegative={false}
            />
            <NumericTextFieldFormik
              id="read_timeout_seconds"
              label={TEXTS.dialog.readTimeout}
              className={classes.textField}
              decimalScale={0}
              allowNegative={false}
            />
            <NumericTextFieldFormik
              id="max_tries"
              label={TEXTS.dialog.maxTries}
              className={classes.textField}
              decimalScale={0}
              allowNegative={false}
            />
          </div>
          <div>
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
              {TEXTS.dialog.operations}
            </Text>
            {isEmpty(operations) ? (
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                {TEXTS.noOperations}
              </Text>
            ) : (
              <div className="grid grid-cols-2">
                {operations.map(({ operation, label }) => (
                  <SwitchFormik key={operation} id={`operations.${operation}`} label={label} hideErrorGap />
                ))}
              </div>
            )}
          </div>
          <AlertBanner alertType={AlertBanner.ALERT_TYPES.INFO} note={TEXTS.dialog.saveNote} withIcon />
        </div>
        <div className="mt-20 flex w-full justify-end">
          <CancelButton disabled={isSubmitting} onClick={onClose} />
          <Button variant="contained" color="primary" disabled={isSubmitting} type="submit">
            {TEXTS.dialog.save}
          </Button>
        </div>
      </Form>
    </CardDialog>
  );
};

export default SorSetupDialog;
